import { useState, useEffect } from 'react';
import { Bar, Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { countyService, CountyData, CropData } from '../services/countyService';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
);

type SearchMode = 'county' | 'crop';

const CountyInfo: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCounty, setSelectedCounty] = useState<CountyData | null>(null);
  const [selectedCrop, setSelectedCrop] = useState<CropData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchMode, setSearchMode] = useState<SearchMode>('county');

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;

    setLoading(true);
    setError(null);

    try {
      if (searchMode === 'county') {
        const data = await countyService.getCountyByName(searchQuery);
        setSelectedCounty(data);
        setSelectedCrop(null);
      } else {
        const data = await countyService.getCropData(searchQuery);
        setSelectedCrop(data);
        setSelectedCounty(null);
      }
    } catch (err) {
      setError(`Failed to fetch ${searchMode} data. Please try again.`);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const generateMarketPriceChart = () => {
    if (!selectedCounty?.marketPrices || selectedCounty.marketPrices.length === 0) return null;

    const data = {
      labels: selectedCounty.marketPrices.map(price => price.commodity),
      datasets: [
        {
          label: 'Market Prices (KES)',
          data: selectedCounty.marketPrices.map(price => price.price),
          backgroundColor: [
            'rgba(54, 162, 235, 0.6)',
            'rgba(75, 192, 192, 0.6)',
            'rgba(255, 206, 86, 0.6)',
            'rgba(255, 99, 132, 0.6)',
            'rgba(153, 102, 255, 0.6)',
            'rgba(255, 159, 64, 0.6)',
            'rgba(199, 199, 199, 0.6)'
          ],
          borderColor: [
            'rgba(54, 162, 235, 1)',
            'rgba(75, 192, 192, 1)',
            'rgba(255, 206, 86, 1)',
            'rgba(255, 99, 132, 1)',
            'rgba(153, 102, 255, 1)',
            'rgba(255, 159, 64, 1)',
            'rgba(199, 199, 199, 1)'
          ],
          borderWidth: 1
        }
      ]
    };

    const options = {
      responsive: true,
      plugins: {
        legend: {
          position: 'top' as const
        },
        title: {
          display: true,
          text: 'Market Prices by Commodity'
        }
      },
      scales: {
        y: {
          beginAtZero: true,
          title: {
            display: true,
            text: 'Price (KES)'
          }
        }
      }
    };

    return <Bar data={data} options={options} />;
  };

  const generateCropCountyComparisonChart = () => {
    if (!selectedCrop) return null;

    const countyNames = Object.keys(selectedCrop.countyData);
    
    const data = {
      labels: countyNames,
      datasets: [
        {
          label: 'Yield Score (0-100)',
          data: countyNames.map(county => selectedCrop.countyData[county]),
          backgroundColor: 'rgba(75, 192, 192, 0.6)',
          borderColor: 'rgba(75, 192, 192, 1)',
          borderWidth: 1
        }
      ]
    };

    const options = {
      responsive: true,
      plugins: {
        legend: {
          position: 'top' as const
        },
        title: {
          display: true,
          text: `${selectedCrop.cropName} Performance by County`
        }
      },
      scales: {
        y: {
          beginAtZero: true,
          max: 100,
          title: {
            display: true,
            text: 'Yield Score (0-100)'
          }
        }
      }
    };

    return <Bar data={data} options={options} />;
  };

  const generateInternationalComparisonChart = () => {
    if (!selectedCrop) return null;

    const countries = Object.keys(selectedCrop.internationalComparison);
    
    const data = {
      labels: countries,
      datasets: [
        {
          label: 'Yield Score (0-100)',
          data: countries.map(country => selectedCrop.internationalComparison[country]),
          backgroundColor: 'rgba(54, 162, 235, 0.6)',
          borderColor: 'rgba(54, 162, 235, 1)',
          borderWidth: 1
        }
      ]
    };

    const options = {
      responsive: true,
      plugins: {
        legend: {
          position: 'top' as const
        },
        title: {
          display: true,
          text: `${selectedCrop.cropName} International Comparison`
        }
      },
      scales: {
        y: {
          beginAtZero: true,
          max: 100,
          title: {
            display: true,
            text: 'Yield Score (0-100)'
          }
        }
      }
    };

    return <Bar data={data} options={options} />;
  };

  const generateMarketTrendChart = () => {
    if (!selectedCrop) return null;
    
    const data = {
      labels: selectedCrop.marketTrend.map(item => item.month),
      datasets: [
        {
          label: 'Price Trend (KES)',
          data: selectedCrop.marketTrend.map(item => item.price),
          borderColor: 'rgb(255, 99, 132)',
          backgroundColor: 'rgba(255, 99, 132, 0.5)',
          tension: 0.2
        }
      ]
    };

    const options = {
      responsive: true,
      plugins: {
        legend: {
          position: 'top' as const
        },
        title: {
          display: true,
          text: `${selectedCrop.cropName} Market Price Trend (Annual)`
        }
      },
      scales: {
        y: {
          beginAtZero: false,
          title: {
            display: true,
            text: 'Price (KES)'
          }
        }
      }
    };

    return <Line data={data} options={options} />;
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="mb-8">
        <div className="flex flex-col gap-4">
          <div className="flex gap-4">
            <button
              onClick={() => setSearchMode('county')}
              className={`px-4 py-2 rounded ${
                searchMode === 'county'
                  ? 'bg-green-600 text-white'
                  : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
              }`}
            >
              Search by County
            </button>
            <button
              onClick={() => setSearchMode('crop')}
              className={`px-4 py-2 rounded ${
                searchMode === 'crop'
                  ? 'bg-green-600 text-white'
                  : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
              }`}
            >
              Search by Crop
            </button>
          </div>
          
          <div className="flex gap-4">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={searchMode === 'county' ? "Enter county name..." : "Enter crop name..."}
              className="flex-1 p-2 border rounded"
            />
            <button
              onClick={handleSearch}
              disabled={loading}
              className="bg-green-600 text-white px-6 py-2 rounded hover:bg-green-700 disabled:opacity-50"
            >
              {loading ? 'Searching...' : 'Search'}
            </button>
          </div>
          {error && <p className="text-red-500 mt-2">{error}</p>}
        </div>
      </div>

      {selectedCounty && (
        <div className="space-y-8">
          <div className="text-xs text-gray-500 mb-4">
            {selectedCounty.dataSource || 'Data sourced from public agricultural databases and government resources.'} Updated {selectedCounty?.lastUpdated ? new Date(selectedCounty.lastUpdated).toLocaleDateString() : 'regularly'}.
          </div>
          
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-2xl font-bold mb-4">{selectedCounty.name} County</h2>
            <p className="text-gray-600">Region: {selectedCounty.region}</p>
            
            <div className="mt-4">
              <h3 className="text-xl font-semibold mb-2">Climate</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-gray-50 p-4 rounded">
                  <p className="font-medium">Temperature</p>
                  <p>{selectedCounty.climate.temperature}</p>
                </div>
                <div className="bg-gray-50 p-4 rounded">
                  <p className="font-medium">Rainfall</p>
                  <p>{selectedCounty.climate.rainfall}</p>
                </div>
                <div className="bg-gray-50 p-4 rounded">
                  <p className="font-medium">Altitude</p>
                  <p>{selectedCounty.climate.altitude}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-xl font-semibold mb-4">Main Crops</h3>
            <div className="flex flex-wrap gap-2">
              {selectedCounty.mainCrops.map((crop, index) => (
                <span key={index} className="bg-green-100 text-green-800 px-3 py-1 rounded-full">
                  {crop}
                </span>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-xl font-semibold mb-4">Market Prices</h3>
            <div className="mb-6 h-80">
              {generateMarketPriceChart()}
            </div>
            <div className="mt-4 overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr>
                    <th className="px-4 py-2">Commodity</th>
                    <th className="px-4 py-2">Price (KES)</th>
                    <th className="px-4 py-2">Unit</th>
                    <th className="px-4 py-2">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedCounty.marketPrices.map((price, index) => (
                    <tr key={index} className={index % 2 === 0 ? 'bg-gray-50' : ''}>
                      <td className="px-4 py-2">{price.commodity}</td>
                      <td className="px-4 py-2">{price.price}</td>
                      <td className="px-4 py-2">{price.unit}</td>
                      <td className="px-4 py-2">{new Date(price.date).toLocaleDateString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-xl font-semibold mb-4">Government Requirements</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <h4 className="font-medium mb-2">Licenses</h4>
                <ul className="list-disc list-inside">
                  {selectedCounty.governmentRequirements.licenses.map((license, index) => (
                    <li key={index}>{license}</li>
                  ))}
                </ul>
              </div>
              <div>
                <h4 className="font-medium mb-2">Permits</h4>
                <ul className="list-disc list-inside">
                  {selectedCounty.governmentRequirements.permits.map((permit, index) => (
                    <li key={index}>{permit}</li>
                  ))}
                </ul>
              </div>
              <div>
                <h4 className="font-medium mb-2">Regulations</h4>
                <ul className="list-disc list-inside">
                  {selectedCounty.governmentRequirements.regulations.map((regulation, index) => (
                    <li key={index}>{regulation}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-xl font-semibold mb-4">Farming Tips</h3>
            <div className="space-y-4">
              {selectedCounty.farmingTips.map((category, index) => (
                <div key={index}>
                  <h4 className="font-medium mb-2">{category.category}</h4>
                  <ul className="list-disc list-inside">
                    {category.tips.map((tip, tipIndex) => (
                      <li key={tipIndex}>{tip}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-xl font-semibold mb-4">Resources</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {selectedCounty.resources.map((resource, index) => (
                <div key={index} className="border p-4 rounded">
                  <h4 className="font-medium mb-2">{resource.title}</h4>
                  <p className="text-gray-600 mb-2">{resource.description}</p>
                  <a
                    href={resource.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline"
                  >
                    Learn More
                  </a>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {selectedCrop && (
        <div className="space-y-8">
          <div className="text-xs text-gray-500 mb-4">
            {selectedCrop.dataSource}
          </div>
          
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-2xl font-bold mb-4">{selectedCrop.cropName}</h2>
            
            <div className="mt-6 mb-8">
              <h3 className="text-xl font-semibold mb-4">County Performance Comparison</h3>
              <div className="h-96">
                {generateCropCountyComparisonChart()}
              </div>
              <p className="text-sm text-gray-600 mt-2">
                Higher scores indicate better growing conditions and yield potential in that county
              </p>
            </div>
            
            <div className="mt-8 mb-8">
              <h3 className="text-xl font-semibold mb-4">International Comparison</h3>
              <div className="h-80">
                {generateInternationalComparisonChart()}
              </div>
              <p className="text-sm text-gray-600 mt-2">
                Comparing Kenya's {selectedCrop.cropName} production with other countries
              </p>
            </div>
            
            <div className="mt-8 mb-8">
              <h3 className="text-xl font-semibold mb-4">Market Price Trend</h3>
              <div className="h-80">
                {generateMarketTrendChart()}
              </div>
              <p className="text-sm text-gray-600 mt-2">
                Annual price fluctuations allow planning for optimal planting and selling time
              </p>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-xl font-semibold mb-4">Growing Conditions</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {Object.entries(selectedCrop.growingConditions).map(([condition, value], index) => (
                <div key={index} className="bg-gray-50 p-4 rounded">
                  <p className="font-medium">{condition}</p>
                  <p>{value}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-xl font-semibold mb-4">Best Practices</h3>
            <div className="space-y-2">
              <ul className="list-disc list-inside">
                {selectedCrop.bestPractices.map((practice, index) => (
                  <li key={index} className="mb-2">{practice}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CountyInfo; 